import { AIRLINE_CODE_LE, FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES } from 'constants/flight'
import { AMADEUS, JETSTAR, SABRE, TRAVEL_FUSION } from 'constants/provider'
import { get, set } from 'lib/storage/isomorphicLocalStorage'
import type { FlightSearchItinerary } from 'api/flights'
import stableStringifyForCacheKey from 'lib/object/stableStringifyForCacheKey'

const CACHE_KEY = 'standalone-charter-quotes-v1'
const TTL_MS = FLIGHTS_SESSION_EXPIRATION_TIME_IN_MINUTES * 60_000
export const MIN_CHARTER_QUOTE_REMAINING_TIME_MS = 5 * 60_000
const MAX_ENTRIES = 16
const OTHER_PROVIDERS = new Set([AMADEUS, JETSTAR, SABRE, TRAVEL_FUSION, 'jetstarV2'])

interface CacheEntry<T> {
  key: string
  result: T
  timestamp: number
}

interface CharterQuotePayload {
  source?: string
  filters?: { airlines?: Array<string> }
  itinerary: Array<FlightSearchItinerary>
  excludeProviders?: Array<string>
  [key: string]: unknown
}

/** Only charter-filtered standalone searches can reuse a recommendation quote. */
export function getCharterQuoteCacheKey(payload: CharterQuotePayload): string | undefined {
  if (payload.source || payload.filters?.airlines?.length !== 1 || payload.filters.airlines[0] !== AIRLINE_CODE_LE ||
    payload.itinerary[0]?.departureAirport !== 'MEL' || payload.itinerary[0]?.arrivalAirport !== 'MLE' ||
    payload.excludeProviders?.some(provider => !OTHER_PROVIDERS.has(provider))) return undefined
  // Excluding other providers does not change a search filtered to the charter airline.
  const { excludeProviders, enableSabreContent, flightsCredit, viewType, ...search } = payload
  // Match the JSON request semantics (omit undefined), independent of property order.
  const normalized: unknown = JSON.parse(JSON.stringify({ ...search, flightsCredit: flightsCredit || undefined, viewType: viewType ?? (payload.tripType === 'OneWay' ? 'OneWay' : undefined) }))
  return stableStringifyForCacheKey(normalized)
}

export function getCachedCharterQuote<T>(key: string | undefined, minimumRemainingTimeMs = 0): CacheEntry<T> | undefined {
  if (!key || typeof window === 'undefined') return undefined
  try {
    const entries = get(CACHE_KEY)
    if (!Array.isArray(entries)) return undefined
    const now = Date.now()
    return entries.find((entry: CacheEntry<T>) => entry?.key === key &&
      Number.isFinite(entry.timestamp) && now >= entry.timestamp && now - entry.timestamp < TTL_MS &&
      entry.timestamp + TTL_MS - now >= minimumRemainingTimeMs)
  } catch {
    return undefined
  }
}

export function cacheCharterQuote<T>(key: string | undefined, result: T, timestamp: number): void {
  if (!key || typeof window === 'undefined') return
  try {
    const stored = get(CACHE_KEY)
    const entries: Array<CacheEntry<T>> = Array.isArray(stored) ? stored : []
    const fresh = entries.filter(entry => entry?.key !== key && Date.now() - entry.timestamp < TTL_MS)
    set(CACHE_KEY, [...fresh.slice(-(MAX_ENTRIES - 1)), { key, result, timestamp }])
  } catch {
    // Storage may be unavailable or full; the network result remains usable.
  }
}
